import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Alert, IconButton, Chip, CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, ReportProblem as ReportProblemIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { studentAPI } from '../../services/api';

const GradeReclamationDialog = ({ open, target, activeSession, onClose, onSubmitted }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  if (!target) return null;

  const { grade, baseCode, studyYear } = target;
  const semester = `S${grade.semester_number}`;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await studentAPI.submitReclamation({
        type: 'grade_reclamation',
        subject: `${t('recl_subjectPrefix')} ${grade.lib_elp || baseCode}`,
        description: reason.trim(),
        module_code: baseCode,
        academic_year: studyYear,
        semester,
        exam_session_id: activeSession?.id,
        reported_grade: grade.cod_tre === 'AB' ? 'AB' : '0',
      });
      onSubmitted();
    } catch (err) {
      setError(err.response?.data?.error || t('recl_submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReportProblemIcon color="warning" />
          {t('recl_dialogTitle')}
        </Box>
        <IconButton size="small" onClick={onClose} disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <Typography variant="body2" fontWeight={700}>
            {grade.lib_elp || baseCode}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
            <Chip label={baseCode} size="small" sx={{ height: 22, fontFamily: 'monospace', fontWeight: 600 }} />
            <Chip label={semester} size="small" sx={{ height: 22, fontWeight: 600 }} />
            <Chip
              label={grade.cod_tre === 'AB' ? t('gt_AB') : '0 / 20'}
              size="small"
              color="error"
              sx={{ height: 22, fontWeight: 700 }}
            />
          </Box>
        </Box>

        <TextField
          label={`${t('recl_reasonLabel')} (${t('optional')})`}
          placeholder={t('recl_reasonPlaceholder')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={4}
          fullWidth
          disabled={submitting}
        />
        <Typography
          variant="caption"
          sx={{
            display: 'block', mt: 0.75, px: 0.25,
            color: '#92400e', lineHeight: 1.6,
            direction: 'rtl', textAlign: 'right',
          }}
        >
          يُمنع تضمين أي عبارات مسيئة أو تشهيرية أو وقائع غير صحيحة، تحت طائلة اتخاذ الإجراءات المنصوص عليها في القوانين والأنظمة المعمول بها.
        </Typography>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          {t('recl_cancel')}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {t('recl_submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GradeReclamationDialog;
